/**
 * Shared utilities for contentEditable paragraph elements used across all
 * Review Documents (RD_*) pages and RmsDocumentReview.
 *
 * These pages render AI-generated report paragraphs inside contentEditable
 * <p> elements. Clinicians edit the plain-text content directly; the text is
 * synced back to the data model via each page's own onInput handler.
 *
 * Previously, each file duplicated its own onPaste and onKeyDown handlers
 * inline (~17 copies). This module centralises them so fixes and improvements
 * propagate everywhere.
 */

/**
 * Paste handler for contentEditable paragraphs that inserts plain text at the
 * current cursor position instead of replacing all existing content.
 *
 * Background: the previous implementation used the async Clipboard API
 * (navigator.clipboard.readText()), which introduced a race condition --
 * the browser selection/caret could be lost between the synchronous
 * preventDefault() call and the async callback, causing
 * selection.deleteFromDocument() to wipe the entire paragraph. This version
 * uses the synchronous e.clipboardData API instead, which executes in the
 * same turn and guarantees the selection is intact.
 *
 * If the cursor is not inside the target element (or no selection exists),
 * the pasted text is appended at the end of the element's content rather
 * than silently failing or inserting into the wrong location.
 *
 * Also strips any rich-text (HTML) formatting from the clipboard, keeping
 * paragraph content as plain text -- intentional for clinical report drafts.
 *
 * Undo/redo: inserting via Range.insertNode() is a silent DOM mutation, so
 * the browser does not put it on the contentEditable undo stack. Typing
 * still undoes with Cmd/Ctrl-Z, but a paste would not. `insertText` is the
 * same command the browser uses for typing, so paste, undo, and redo share
 * one history. (execCommand is deprecated, but it is still the only widely
 * supported way to insert into contentEditable with native undo.)
 *
 * @param {ClipboardEvent} e - The paste event from the contentEditable element
 */
export function handlePlainTextPaste(e) {
  e.preventDefault();

  const text = e.clipboardData?.getData('text/plain');
  if (!text) return;

  const target = e.currentTarget || e.target;
  const selection = window.getSelection();

  // Check whether the current selection/caret lives inside our
  // contentEditable element. If not (e.g. the user clicked elsewhere on the
  // page before pasting, or the caret was never placed), fall back to
  // appending at the end of the paragraph.
  const selectionIsInTarget =
    selection.rangeCount > 0 &&
    target.contains(selection.getRangeAt(0).startContainer);

  if (!selectionIsInTarget) {
    target.focus();
    const range = document.createRange();
    range.selectNodeContents(target);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  // insertText replaces any highlighted selection and records one undo step.
  // Native insertText also fires the input event, so we must not dispatch a
  // second one on this path (that would double-run each page's onInput).
  if (insertTextWithUndo(text)) return;

  // Fallback when insertText is unavailable (jsdom, some older engines).
  // This path is not undoable — same as the previous paste implementation.
  selection.deleteFromDocument();

  const range = selection.getRangeAt(0);
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);

  // Move the cursor to immediately after the pasted content so the user
  // can continue typing seamlessly.
  range.setStartAfter(textNode);
  range.setEndAfter(textNode);
  selection.removeAllRanges();
  selection.addRange(range);

  // Dispatch a synthetic input event so each page's own onInput handler
  // picks up the change (e.g. writing to REPORT_DATA_OUTPUTS, repositioning
  // the floating action panel, etc.).
  const inputEvent = new Event('input', { bubbles: true });
  target.dispatchEvent(inputEvent);
}

/**
 * Inserts plain text at the current selection using the browser's typing
 * command, so Cmd/Ctrl-Z / Shift-Z can undo and redo the paste.
 *
 * Returns true when the browser accepted the command. False means the
 * caller should fall back to a manual DOM insert.
 *
 * @param {string} text
 * @returns {boolean}
 */
function insertTextWithUndo(text) {
  try {
    return document.execCommand('insertText', false, text);
  } catch {
    return false;
  }
}

/**
 * Prevents browser formatting shortcuts (Ctrl/Cmd + B, I, U) on
 * contentEditable elements, keeping paragraph content as plain text.
 *
 * Without this, a clinician could accidentally bold/italicise text inside
 * a paragraph that should only contain plain text. The formatted HTML would
 * then be written back to Firestore and potentially break downstream
 * rendering (report generation, LLM pipelines).
 *
 * @param {KeyboardEvent} e - The keydown event from the contentEditable element
 */
export function suppressFormattingShortcuts(e) {
  if ((e.ctrlKey || e.metaKey) && ['b', 'i', 'u'].includes(e.key)) {
    e.preventDefault();
  }
}
